import { useCallback, useLayoutEffect, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ArrowRight, MessageSquareText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  defineStudioTool,
  selectStudioTool,
  useStudio,
  useStudioShell,
  useStudioTools,
  type StudioTool,
} from '@okrapdf/pdf-studio-plugin/studio';

import { isClerkEnabled } from '@/components/auth-gate';
import { Button } from '@/components/ui';
import { ToolCommandPalette } from '@/features/tools';
import { BrandLogo } from '@/lib/brand/logo';
import { isAppHost } from '@/lib/host';
import { capture } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import { HOST_EXTRA_TOOLS, paletteToolGroups } from './extra-tools';
import { studioAgentStore } from './studio-agent-store';
import {
  studioAgentEnabled,
  type IntentPart,
  type IntentResult,
  type IntentTool,
} from './studio-intent';

/**
 * Okra Agent as a Studio tool (W.4.105): the tool definition, the top-bar
 * button that opens it, the reply both the home composer and the Agent panel
 * render, and the ⌘K palette that hands it what no tool matches. Since W.4.106
 * the Agent reads the open PDF in its workspace and answers from it; it still
 * opens tools rather than running them.
 */

export const OKRA_AGENT_TOOL_ID = 'okra-agent';

const MAX_REPLY_ALTERNATIVES = 3;

const okraAgentTool = defineStudioTool({
  id: OKRA_AGENT_TOOL_ID,
  title: 'Okra Agent',
  // Shown above the panel on the home too, where no PDF is open (W.4.132): it names none.
  description:
    'Ask about a PDF or say what you need. Okra Agent reads the PDF you open in its workspace and opens the right tool.',
  icon: MessageSquareText,
  category: 'ai',
  keywords: ['agent', 'ask', 'help', 'assistant'],
  // It opens on the home too, where it holds the text a tool search left for it and says to
  // open a PDF: a chat belongs to its PDF (W.4.132), so with none open it shows none.
  requiresDocument: false,
  hideFromHomeGrid: true,
  load: () => import('./extra-tools/panels/okra-agent-panel'),
});

/**
 * The signed-in Studio's extra tools. A separate list from `HOST_EXTRA_TOOLS`
 * because the public Studio mounts that one, and the Agent needs an account.
 */
export const APP_STUDIO_EXTRA_TOOLS: StudioTool[] = [...HOST_EXTRA_TOOLS, okraAgentTool];

/**
 * The extra tools for this deploy: the Agent is left out where it has no
 * backend. Returns one of two fixed lists, so the Studio sees a stable prop.
 */
export function appStudioExtraTools(): StudioTool[] {
  return studioAgentEnabled() ? APP_STUDIO_EXTRA_TOOLS : HOST_EXTRA_TOOLS;
}

function useClerkAgentOwner(): void {
  const { isLoaded, userId } = useAuth();
  // A layout effect, not a passive one: an Agent panel restored by `?tool=`
  // mounts in the same commit and must not paint another account's exchanges.
  useLayoutEffect(() => {
    if (isLoaded) studioAgentStore.setOwner(userId ?? null);
  }, [isLoaded, userId]);
}

function useNoAgentOwner(): void {}

/**
 * Keep the Agent's exchanges with the account that wrote them. Sign-out and
 * sign-in do not reload the page, so without this the next account in the tab
 * would read the previous one's prompts.
 */
export function useAgentSessionOwner(): void {
  // isClerkEnabled() reads a build-time constant, so the same hook runs on
  // every render; the branch keeps useAuth() out of a boot with no ClerkProvider.
  const useOwner = isClerkEnabled() ? useClerkAgentOwner : useNoAgentOwner;
  useOwner();
}

function useClerkSignedIn(): boolean {
  return useAuth().isSignedIn === true;
}

// With no Clerk the app's AuthGate lets every visitor in.
function useNoClerkSignedIn(): boolean {
  return true;
}

/**
 * Whether this visitor gets the Agent: signed in, on the app host, on a deploy
 * that has it. The public site offers none: it needs an account. The free
 * workbench goes by this too, so a Studio that offers the Agent always holds it.
 */
export function useAgentOffered(): boolean {
  // Same build-time branch as `useAgentSessionOwner`.
  const useSignedIn = isClerkEnabled() ? useClerkSignedIn : useNoClerkSignedIn;
  const signedIn = useSignedIn();
  return signedIn && isAppHost() && studioAgentEnabled();
}

/**
 * The ⌘K palette, mounted once at the app root. A query no tool matches opens
 * the Agent with the text in its message box, where the visitor sends it.
 */
export function AppToolPalette() {
  const navigate = useNavigate();
  const agentOffered = useAgentOffered();
  const catalogue = useMemo(() => paletteToolGroups(), []);

  const askAgent = useCallback(
    (text: string) => {
      // The event says the Agent was picked. The text is a prompt and stays out of it.
      capture('tool_palette_tool_selected', {
        tool: OKRA_AGENT_TOOL_ID,
        category: 'ai',
        has_query: true,
      });
      studioAgentStore.fillComposer(text);
      // A Studio takes it in place, so the open document and its edits stay and
      // the question stays with the file. Only a page with no Studio leaves.
      if (!selectStudioTool(OKRA_AGENT_TOOL_ID)) navigate(`/tools?tool=${OKRA_AGENT_TOOL_ID}`);
    },
    [navigate],
  );

  return <ToolCommandPalette catalogue={catalogue} onAsk={agentOffered ? askAgent : undefined} />;
}

/**
 * The catalogue a prompt is routed over: every visible tool but the `ai` ones,
 * so the Agent can never answer with itself.
 */
export function useIntentTools(): IntentTool[] {
  const { tools } = useStudioTools();
  return useMemo(
    () =>
      tools
        .filter((tool) => tool.category !== 'ai')
        .map(({ id, title, description, keywords, requiresDocument }) => ({
          id,
          title,
          description,
          keywords,
          requiresDocument,
        })),
    [tools],
  );
}

/** Top-bar toggle for the Agent panel. The brand mark is the Agent's icon. */
export function AgentTopBarButton() {
  const { activeToolId, setActiveTool } = useStudio();
  const { selectTool } = useStudioShell();
  const active = activeToolId === OKRA_AGENT_TOOL_ID;

  return (
    <Button
      variant="outline"
      size="sm"
      aria-label="Okra Agent"
      aria-pressed={active}
      onClick={() => (active ? setActiveTool(null) : selectTool(OKRA_AGENT_TOOL_ID))}
      // Sized to the bar's own Share button, which sits right beside it.
      className={cn(
        'h-auto gap-1.5 px-2.5 py-1.5',
        active
          ? 'border-selected-line bg-selected text-selected-foreground hover:bg-selected hover:text-selected-foreground'
          : 'border-border bg-card text-foreground',
      )}
    >
      <BrandLogo decorative size={16} className="rounded-sm" />
      <span className="hidden sm:inline">Agent</span>
    </Button>
  );
}

/**
 * The button that opens a tool the Agent picked. Shared by the routed reply
 * and the workspace answer, so "Open <Tool>" looks and acts the same in both.
 *
 * It carries the tool's own title and nothing the model wrote. The Agent's
 * reason for proposing a tool is written after reading the PDF, and an
 * attribute is not masked: session replay records it and a click sends it
 * with the event.
 */
export function OpenToolButton({
  tool,
  onOpenTool,
}: {
  tool: Pick<IntentTool, 'id' | 'title'>;
  onOpenTool: (id: string) => void;
}) {
  return (
    <Button
      variant="secondary"
      size="sm"
      aria-label={`Open ${tool.title}`}
      onClick={() => onOpenTool(tool.id)}
      // A long tool title wraps inside the button rather than widening the panel.
      className="h-auto min-h-8 max-w-full whitespace-normal py-1.5 text-left"
    >
      Open {tool.title}
      <ArrowRight aria-hidden="true" />
    </Button>
  );
}

/** What does one request of the prompt, in words. */
function partLabel(part: IntentPart, tools: IntentTool[]): string {
  const tool = part.toolId ? tools.find((candidate) => candidate.id === part.toolId) : undefined;
  if (tool) return tool.title;
  return part.agent ? 'Okra Agent can answer this once it has the PDF' : 'No tool here does this';
}

/**
 * One routed answer: the sentence, the tool it picked, and up to three other
 * tools. Ids are resolved against `tools`; one the host does not have is
 * dropped, so a reply can never offer a tool that would not open. A prompt
 * that asked for several things gets a line per request (W.4.132), the ones
 * no tool does included.
 */
export function IntentReply({
  result,
  tools,
  onOpenTool,
}: {
  result: IntentResult;
  tools: IntentTool[];
  onOpenTool: (id: string) => void;
}) {
  const main = tools.find((tool) => tool.id === result.toolId) ?? null;
  const alternatives: IntentTool[] = [];
  for (const id of result.alternatives) {
    if (alternatives.length === MAX_REPLY_ALTERNATIVES) break;
    const tool = tools.find((candidate) => candidate.id === id);
    if (tool && tool !== main && !alternatives.includes(tool)) alternatives.push(tool);
  }

  return (
    <div className="space-y-2">
      {/* Masked in session replay: the Agent may repeat the prompt or the file name. */}
      <p data-ph-mask className="break-words text-sm text-foreground">
        {result.reply}
      </p>
      {result.parts?.length ? (
        // The requests are the visitor's words, so session replay masks the list.
        <ul data-ph-mask aria-label="Each request" className="space-y-1 text-sm">
          {result.parts.map((part, index) => (
            <li key={index} className="flex flex-wrap gap-x-1.5">
              {/* A pasted link is one long word: it wraps inside the card at phone width. */}
              <span className="min-w-0 break-words text-foreground">“{part.request}”</span>
              <span className="text-muted-foreground">— {partLabel(part, tools)}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {main ? <OpenToolButton tool={main} onOpenTool={onOpenTool} /> : null}
      {alternatives.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {main ? <span className="text-xs text-muted-foreground">Or</span> : null}
          {alternatives.map((tool) => (
            <Button
              key={tool.id}
              variant="outline"
              size="sm"
              title={tool.description}
              onClick={() => onOpenTool(tool.id)}
              className="h-7 rounded-full bg-card px-3"
            >
              {tool.title}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
